import React, { useState, useEffect } from 'react';
import { SessionPeriodChip } from '../components/UI/SessionPeriodChip';
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  FileText,
  User,
  Plus,
  CheckCircle,
  AlertCircle,
  CalendarCheck,
  TrendingUp,
  DollarSign,
  X,
  XCircle,
  Wallet,
  CalendarDays,
  CalendarX,
  Heart,
  Baby,
  Thermometer,
  Hospital,
  Plane,
  AlertTriangle,
  BarChart3,
  Eye,
  ArrowRight,
  Home,
  MapPinned,
  Hourglass,
  Gem,
  Package,
  GraduationCap,
  Shield,
  Sun,
  ClipboardCheck,
  Moon,
  RotateCcw,
  Users,
  Vault,
  Loader2,
  ScrollText,
} from 'lucide-react';
import { useWorkPassCapabilities } from '../hooks/useWorkPassDecisions';
import { useTerminationCapabilities } from '../hooks/useTerminationRequests';
import { staffAPI } from '../services/api';
import { resolveLeaveStatus, sameLeaveApplicationId } from '../utils/leavePermissions';
import { fetchRoPendingApprovals, isAcknowledgedNsLeave, isNationalServiceLeave } from '../utils/pendingLeaveInbox';
import { formatDate, formatDateTime, formatDateShort, getYearInSingapore, getTodayInSingaporeTimezone } from '../utils/dateUtils';
import { formatLeaveDays, formatLeaveDaysEmployee, pluralizeDays } from '../utils/numberUtils';
import { getWFHReasonLabel } from '../utils/leaveUtils';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Portal from '../components/UI/Portal';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import DocumentVerificationModal from '../components/DocumentVerificationModal';
import NSDocumentVerificationModal from '../components/NSDocumentVerificationModal';
import MCDocumentVerificationModal from '../components/MCDocumentVerificationModal';
import FlightTicketVerificationModal from '../components/FlightTicketVerificationModal';
import ROExceptionModal from '../components/ROExceptionModal';
import { StatusBadge } from '../components/UI/StatusBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import { CountBadge } from '../components/UI/CountBadge';
import IconButton from '../components/UI/IconButton';
import {
  hasCrossTypeConsumption as hasCrossType,
  getAppliedLeaveTypeName,
  getDisplayTotalDays,
  getLeavePeriodDisplayChips,
  getNextAccrualDate,
  formatAccrualDate,
  formatOilCreditDaysLabel,
  getOilCreditDaysClassName,
  buildOilExpiryHint,
} from '../utils/leaveDisplayUtils';
import { keepLatestIdCardPerSide } from '../config/documentTypes';
import { shouldHideDashboardCompassionateCard } from '../constants/leaveTypes';
import { OverseasTravelDeclarationSummary } from '../components/Leave/OverseasTravelDeclarationFields';
import OilClaimWorkDates from '../components/Leave/OilClaimWorkDates';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAnnualLeaveYearEndPlan } from '../hooks/useLeaveData';
import AnnualLeaveYearEndBalance from '../components/Leave/AnnualLeaveYearEndBalance';
import ConsumptionBreakdown from '../components/Leave/ConsumptionBreakdown';

// Helper function for progress bar width calculation
const calculateProgressBarWidth = (balance, total, remainingPercentage) => {
  // All leave types (including Annual Leave) use remaining percentage
  // This shows what percentage of total available days remain (including carry-forward)
  return isFinite(remainingPercentage) ? Math.min(Math.max(remainingPercentage, 0), 100) : 0;
};

// Static per-leave-type configuration for the RO approval modal.
// Each entry declares which content sections to render and whether to show the compliance panel.
const LEAVE_TYPE_CONFIG = {
  'Claim: Off in Lieu':     { sections: ['oil_claim'],        showCompliance: true  },
  'Off in Lieu':            { sections: ['oil_usage'],         showCompliance: true  },
  'Annual Leave':           { sections: ['reason'],            showCompliance: true  },
  'Work From Home':         { sections: ['wfh_reason'],        showCompliance: true  },
  'Childcare Leave':        { sections: ['reason'],            showCompliance: true  },
  'Compassionate Leave':    { sections: ['compassionate'],     showCompliance: false },
  'Outpatient Sick Leave':  { sections: ['sick_info'],         showCompliance: false },
  'Hospitalisation Leave':  { sections: ['sick_info'],         showCompliance: false },
  'National Service Leave': { sections: ['ns_details'],        showCompliance: false },
  'On Course':              { sections: ['on_course'],         showCompliance: false },
  'Maternity Leave':        { sections: ['reason'],            showCompliance: false },
  'Paternity Leave':        { sections: ['reason'],            showCompliance: false },
  // External Activity is auto-approved at submission and never enters the RO pending queue.
};

const getLeaveTypeConfig = (leaveTypeName) => {
  if (!leaveTypeName) return { sections: ['reason'], showCompliance: true };
  if (LEAVE_TYPE_CONFIG[leaveTypeName]) return LEAVE_TYPE_CONFIG[leaveTypeName];
  const lower = leaveTypeName.toLowerCase();
  for (const [key, val] of Object.entries(LEAVE_TYPE_CONFIG)) {
    if (lower.includes(key.toLowerCase())) return val;
  }
  return { sections: ['reason'], showCompliance: true };
};

const NightworkDashboardCard = () => {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['nightwork-dashboard-summary'],
    queryFn: staffAPI.getNightworkDashboardSummary,
    select: (r) => r.data,
    staleTime: 60_000,
  });

  if (isLoading || !summary?.has_nightwork_content) return null;

  const activeAssignment = summary.active_assignment;
  const isRecovery = summary.is_recovery_day;
  const tlPending = summary.tl_pending_count || 0;
  const tlActive = summary.tl_active_count || 0;
  const approverPending = summary.approver_pending_count || 0;
  const approverActive = summary.approver_active_count || 0;
  const adminPending = summary.admin_pending_count || 0;
  const adminActive = summary.admin_active_count || 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-4 border-b bg-indigo-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Moon className="w-5 h-5 text-indigo-700" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900 truncate">Nightwork</h2>
          </div>
          <Link
            to="/self-service/nightwork"
            className="text-xs font-medium text-indigo-700 hover:text-indigo-900 flex items-center gap-1 rounded-full px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex-shrink-0"
          >
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="px-5 py-4 space-y-3">
        {/* Worker: active assignment */}
        {activeAssignment && (
          <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-xl">
            <Clock className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-indigo-900">Active Nightwork Assignment</p>
              <p className="text-indigo-700 mt-0.5">
                {activeAssignment.project_code} &middot; {activeAssignment.start_time} – {activeAssignment.end_time}
              </p>
              <p className="text-indigo-600 text-xs mt-0.5">
                {activeAssignment.remaining_dates} {activeAssignment.remaining_dates === 1 ? 'night' : 'nights'} remaining
                {activeAssignment.next_date && ` · Next: ${new Date(activeAssignment.next_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
              </p>
            </div>
          </div>
        )}

        {/* Recovery day */}
        {isRecovery && (
          <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-xl">
            <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-emerald-900">Recovery Day</p>
              <p className="text-emerald-700 mt-0.5">Rest well — you are not required to work today.</p>
            </div>
          </div>
        )}

        {/* Trade Leader stats */}
        {(tlPending > 0 || tlActive > 0) && (
          <div className="flex items-center gap-4 text-sm">
            {tlPending > 0 && (
              <span className="inline-flex items-center gap-1.5 text-orange-700">
                <span className="w-2 h-2 bg-orange-400 rounded-full" />
                {tlPending} pending approval
              </span>
            )}
            {tlActive > 0 && (
              <span className="inline-flex items-center gap-1.5 text-green-700">
                <span className="w-2 h-2 bg-green-400 rounded-full" />
                {tlActive} active
              </span>
            )}
          </div>
        )}

        {/* Approver stats */}
        {approverPending > 0 && (
          <Link
            to="/self-service/nightwork?tab=pending-approval"
            className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
            <span className="text-sm font-medium text-orange-900">
              {approverPending} nightwork {approverPending === 1 ? 'request' : 'requests'} awaiting your approval
            </span>
          </Link>
        )}

        {approverActive > 0 && (
          <p className="text-xs text-gray-500">{approverActive} active nightwork {approverActive === 1 ? 'assignment' : 'assignments'} in your department</p>
        )}

        {/* Admin stats */}
        {adminPending > 0 && !approverPending && (
          <Link
            to="/self-service/nightwork?tab=pending-approval"
            className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
            <span className="text-sm font-medium text-orange-900">
              {adminPending} pending nightwork {adminPending === 1 ? 'request' : 'requests'}
            </span>
          </Link>
        )}
        {adminActive > 0 && (
          <p className="text-xs text-gray-500">{adminActive} active nightwork {adminActive === 1 ? 'assignment' : 'assignments'} company-wide</p>
        )}
      </div>
    </div>
  );
};

const TradeTransferDashboardCard = () => {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['trade-transfer-dashboard-summary'],
    queryFn: staffAPI.getTradeTransferDashboardSummary,
    select: (r) => r.data,
    staleTime: 60_000,
  });

  if (isLoading || !summary?.has_trade_transfer_content) return null;

  const inboxCount = summary.inbox_count || 0;
  const myInProgress = summary.my_in_progress_count || 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-4 border-b bg-violet-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
              <RotateCcw className="w-5 h-5 text-violet-700" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900 truncate">Worker Trade Transfer</h2>
          </div>
          <Link
            to="/self-service/trade-transfer"
            className="text-xs font-medium text-violet-700 hover:text-violet-900 flex items-center gap-1 rounded-full px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex-shrink-0"
          >
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="px-5 py-4 space-y-3">
        {inboxCount > 0 && (
          <Link
            to={summary.first_pending_item_id
              ? `/self-service/trade-transfer?tt=${summary.first_pending_item_id}`
              : '/self-service/trade-transfer'}
            className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
            <span className="text-sm font-medium text-orange-900">
              {inboxCount} trade transfer {inboxCount === 1 ? 'item awaits' : 'items await'} your action
            </span>
          </Link>
        )}
        {myInProgress > 0 && (
          <p className="text-xs text-gray-500">
            {myInProgress} of your transfer {myInProgress === 1 ? 'request is' : 'requests are'} in progress
          </p>
        )}
      </div>
    </div>
  );
};

const WorkPassDashboardCard = ({ user }) => {
  const { data: caps, isLoading } = useWorkPassCapabilities({
    enabled: !!user?.can_act_work_pass_decision,
  });
  const pending = caps?.pending_work_pass_actions || 0;
  if (isLoading || !user?.can_act_work_pass_decision || pending === 0) return null;
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-4 border-b bg-blue-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-blue-800" />
            </div>
            <div className="min-w-0">
              <h2 className="text-subsection-header truncate">Work Pass Decisions</h2>
              <p className="text-body mt-1 truncate">Retain or end recommendations awaiting you</p>
            </div>
          </div>
          <CountBadge count={pending} theme="warning" size="md" className="ml-2 flex-shrink-0" />
        </div>
      </div>
      <div className="px-5 py-4">
        <Link
          to="/self-service/work-pass-decisions"
          className="flex items-center justify-between gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        >
          <span className="text-sm font-medium text-blue-900">
            {pending} work-pass {pending === 1 ? 'decision awaits' : 'decisions await'} your action
          </span>
          <ArrowRight className="w-4 h-4 text-blue-800 flex-shrink-0" />
        </Link>
      </div>
    </div>
  );
};

const ResignationNoticeDashboardCard = ({ user }) => {
  const { data: caps, isLoading } = useTerminationCapabilities({ enabled: !!user });
  const pending = caps?.pending_resignation_ack || 0;
  if (isLoading || pending === 0) return null;
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-4 border-b bg-blue-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ScrollText className="w-5 h-5 text-blue-800" />
            </div>
            <div className="min-w-0">
              <h2 className="text-subsection-header truncate">Resignation notice</h2>
              <p className="text-body mt-1 truncate">Confirm the dates and that you chose to leave</p>
            </div>
          </div>
          <CountBadge count={pending} theme="warning" size="md" className="ml-2 flex-shrink-0" />
        </div>
      </div>
      <div className="px-5 py-4">
        <Link
          to="/self-service/resignation-notice"
          className="flex items-center justify-between gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        >
          <span className="text-sm font-medium text-blue-900">
            {pending === 1 ? 'Review your resignation notice' : `${pending} resignation notices need review`}
          </span>
          <ArrowRight className="w-4 h-4 text-blue-800 flex-shrink-0" />
        </Link>
      </div>
    </div>
  );
};

const PassportDashboardCard = ({ user }) => {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['passport-return', 'summary'],
    queryFn: staffAPI.getPassportReturnSummary,
    select: (response) => response.data,
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const pendingMyAction = summary?.pending_my_action || 0;

  if (isLoading || !summary?.visible || pendingMyAction === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-4 border-b bg-blue-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Vault className="w-5 h-5 text-blue-800" />
            </div>
            <div className="min-w-0">
              <h2 className="text-subsection-header truncate">Passport Return</h2>
              <p className="text-body mt-1 truncate">Permanent return actions awaiting completion</p>
            </div>
          </div>
          <CountBadge
            count={pendingMyAction}
            theme="warning"
            size="md"
            className="ml-2 flex-shrink-0"
          />
        </div>
      </div>
      <div className="px-5 py-4 space-y-3">
        <Link
          to="/self-service/passport-return"
          className="flex items-center justify-between gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        >
          <span className="text-sm font-medium text-blue-900">
            {pendingMyAction} passport return {pendingMyAction === 1 ? 'action awaits' : 'actions await'} completion
          </span>
          <ArrowRight className="w-4 h-4 text-blue-800 flex-shrink-0" />
        </Link>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const isMobile = useIsMobile();
  // Standardized max-height for all dashboard cards to ensure visual consistency in grid layout
  // Responsive: 300px on mobile, 450px on desktop - prevents white space gaps and ensures proper alignment
  const DASHBOARD_CARD_CONTENT_MAX_HEIGHT = 'max-h-[300px] md:max-h-[450px]';

  const [verificationModal, setVerificationModal] = useState({ open: false, documentApprovalId: null, documentType: null, employeeName: null, employeeStaffCode: null });
  const [nsVerificationModal, setNsVerificationModal] = useState({ open: false, documentApprovalId: null, leaveApplicationId: null, employeeName: null, employeeStaffCode: null });
  const [mcVerificationModal, setMcVerificationModal] = useState({ open: false, applicationId: null, application: null });
  const [flightTicketModal, setFlightTicketModal] = useState({ open: false, documentApprovalId: null, leaveApplication: null });
  const [exceptionModal, setExceptionModal] = useState({ open: false, exception: null });
  const [approvalDetailsModal, setApprovalDetailsModal] = useState({ open: false, leave: null, rejecting: false, rejectionReason: '' });
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
  const queryClient = useQueryClient();
  const { canAccessAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: staffAPI.getCurrentUser,
    select: (response) => response.data,
    staleTime: 0,
    refetchOnMount: true,
  });

  // Cache canAccessAdmin() result to avoid multiple calls per render
  const isAdminAccess = React.useMemo(() => {
    return !!(canAccessAdmin && canAccessAdmin());
  }, [canAccessAdmin, user]);

  // Role flags
  const isAdmin = isAdminAccess;
  // Whether the user holds ANY leave-approval responsibility. Multi-level
  // workflows route to Trade Leaders (L2) and HOD/Deputy HOD (L3) who are
  // not necessarily reporting officers, so gating the "My Approvals" query
  // on RO/manager status alone hides pending items from those approvers.
  const isApprover = !!(
    user?.is_reporting_officer ||
    user?.is_manager ||
    user?.is_trade_leader ||
    user?.is_hod ||
    (user?.team_members?.length > 0)
  );

  // Dashboard stats removed - metrics no longer displayed per design preference
  // const { data: dashboardStats, isLoading: statsLoading } = useQuery({
  //   queryKey: ['dashboardStats'],
  //   queryFn: staffAPI.getDashboardStats,
  //   select: (response) => response.data,
  // });

  const { data: leaveApplications, isLoading: leaveLoading, isError: leaveError } = useQuery({
    queryKey: ['leave', 'my-applications', 'dashboard', user?.staff_id],
    // Fetch 15 so the relevance sort below always has enough candidates to pick 5 from.
    // No date filter — an employee cares about pending and upcoming leaves regardless of when they applied.
    queryFn: () => staffAPI.getMyLeaveApplications({ page_size: 15, ordering: '-leave_start_date' }),
    enabled: !!user,
    staleTime: 60 * 1000,
    select: (response) => {
      const all = response.data?.results || response.data || [];
      const today = getTodayInSingaporeTimezone();

      // Priority: 1 = unresolved (pending/submitted), 2 = upcoming approved, 3 = recent past
      const priority = (leave) => {
        const status = leave.leave_status || leave.status || '';
        const start = leave.leave_start_date || leave.start_date || '';
        if (status === 'submitted' || status === 'pending') return 1;
        if (start >= today) return 2;
        return 3;
      };

      return [...all]
        .sort((a, b) => {
          const pa = priority(a);
          const pb = priority(b);
          if (pa !== pb) return pa - pb;
          // Within same priority: soonest start date first for upcoming; most recent first for past
          const sa = a.leave_start_date || a.start_date || '';
          const sb = b.leave_start_date || b.start_date || '';
          return pa === 3 ? sb.localeCompare(sa) : sa.localeCompare(sb);
        })
        .slice(0, 5);
    },
  });

  const displayName = React.useMemo(() => {
    if (!user) return 'User';
    const alias = user?.alias && String(user.alias).trim();
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    const emailLocal = user?.work_email ? String(user.work_email).split('@')[0] : '';
    return alias || fullName || emailLocal || user?.staff_id || 'User';
  }, [user]);

  // Check if user is a system account (excluded from probation messaging)
  const isSystemAccount = React.useCallback((u) => {
    return ['A0001', 'S0000'].includes(u?.staff_id) 
           || u?.employment_classification === 'system';
  }, []);

  // Calculate completed months of service (MOM-compliant: >= 3 months = entitled)
  const getCompletedServiceMonths = React.useCallback((employmentStartDate) => {
    if (!employmentStartDate) return 0;
    const start = new Date(employmentStartDate);
    const now = new Date();
    const months = (now.getFullYear() - start.getFullYear()) * 12 
                   + (now.getMonth() - start.getMonth());
    // Only count completed months (day of month must have passed)
    return now.getDate() >= start.getDate() ? months : months - 1;
  }, []);

  // FIXED: Use getMyLeaveBalances() - backend now includes eligibility filtering
  // This endpoint now internally uses the same logic as getAvailableLeaveTypes
  // and provides real balance data with proper unpaid leave logic
  const { data: yearEndPlan } = useAnnualLeaveYearEndPlan();
  const { data: leaveBalances, isLoading: balancesLoading } = useQuery({
    queryKey: ['leaveBalances'],
    queryFn: () => staffAPI.getMyLeaveBalances({ 
      year: new Date().getFullYear()
    }),
    select: (response) => {
      const balances = response.data?.results || response.data || [];

      // Determine Annual Leave availability
      const annualLeaveBalance = balances.find(b => b.leave_type?.leave_type_name === 'Annual Leave');
      const annualRemaining = Number(annualLeaveBalance?.remaining_days ?? annualLeaveBalance?.remaining_balance ?? 0);
      const annualEntitled = Number(annualLeaveBalance?.entitled_days ?? annualLeaveBalance?.total_entitlement ?? 0);

      // Filter: hide non-entitled types; show Unpaid Leave when AL unavailable
      let filteredBalances = balances.filter(balance => {
        const name = balance.leave_type?.leave_type_name || '';
        const remaining = Number(balance.remaining_days ?? balance.remaining_balance ?? 0);
        const entitled = Number(balance.entitled_days ?? balance.total_entitlement ?? 0);

        // ALWAYS show sick leave types (even if 0 balance - probation case)
        // Exception: advisors have no sick leave entitlement at all
        if (name.includes('Sick') || name.includes('Hospitalisation')) {
          if (user?.employment_classification === 'advisor') {
            return false;
          }
          return true;
        }

        // Exclude special types that don't have balance tracking (they show in leave application dropdown only)
        const specialNoBalanceTypes = ['Claim: Off in Lieu', 'External Activity', 'Work From Home', 'National Service Leave', 'On Course'];
        if (specialNoBalanceTypes.includes(name)) {
          return false;
        }

        // Hide "Off in Lieu" (usage) when balance is 0 - no point showing an exhausted OIL card
        if (name === 'Off in Lieu' && remaining <= 0) {
          return false;
        }

        if (name === 'Unpaid Leave') {
          return annualRemaining === 0; // show when AL exhausted or not entitled
        }

        // Compassionate leave: hide before first use and after full exhaustion
        if (shouldHideDashboardCompassionateCard(balance)) return false;

        if (remaining === 0 && entitled === 0) {
          return false; // not entitled yet
        }

        return true;
      });

      // Ensure Unpaid Leave card appears if needed and backend omitted it
      const hasUnpaid = filteredBalances.some(b => b.leave_type?.leave_type_name === 'Unpaid Leave');
      if (!hasUnpaid && annualRemaining === 0) {
        filteredBalances.push({
          leave_type: { id: 'unpaid-leave', leave_type_name: 'Unpaid Leave' },
          used_days: 0,
          remaining_days: null,
          entitled_days: null,
          total_available_days: null,
        });
      }

      // Define leave type sort order.
      // Home Leave sits right after Annual Leave for workers — second-most
      // relevant balance (30 cal days / calendar year, MOM-aligned reset on
      // 1 Jan). Hidden automatically for non-workers by the
      // entitled==0 && remaining==0 filter upstream.
      const leaveTypeOrder = {
        'Annual Leave': 1,
        'Home Leave': 1.5,
        'Off in Lieu': 2,  // Show OIL right after Annual Leave
        'Outpatient Sick Leave': 3,
        'Hospitalisation Leave': 4,
        'Maternity Leave': 5,
        'Paternity Leave': 6,
        'Shared Parental Leave': 7,
        'Childcare Leave': 8,
        'Marriage Leave': 9,
        'Compassionate Leave (Immediate Family)': 10,
        'Compassionate Leave (Grandparent)': 10.5,
        'Compassionate Leave': 10, // generic fallback
        'Unpaid Leave': 99,
      };

      // Sort filtered balances by defined order
      filteredBalances.sort((a, b) => {
        const nameA = a.leave_type?.leave_type_name || '';
        const nameB = b.leave_type?.leave_type_name || '';
        
        const orderA = leaveTypeOrder[nameA] || 999;
        const orderB = leaveTypeOrder[nameB] || 999;
        
        return orderA - orderB;
      });
      
      // Transform to match Dashboard's expected format
      const transformedBalances = filteredBalances.map(balance => {
        const name = balance.leave_type?.leave_type_name || 'Unknown';
        const isUnpaid = name === 'Unpaid Leave';
        return {
          leave_type: name,
          entitled_days: isUnpaid ? null : parseFloat(balance.entitled_days || 0),
          used_days: parseFloat(balance.used_days || 0),
          remaining_days: isUnpaid ? null : parseFloat(balance.remaining_days || 0),
          carried_forward_days: isUnpaid ? null : parseFloat(balance.carried_forward_days || 0),
          accrued_days: isUnpaid ? null : parseFloat(balance.accrued_days || 0),
          total_available_days: isUnpaid ? null : parseFloat(balance.total_available_days || balance.entitled_days || 0),
          category: balance.leave_type?.category || 'other',
          is_paid: balance.leave_type?.leave_type_is_paid ?? true,
          requires_mc: balance.leave_type?.leave_type_requires_medical_cert ?? false,
          is_unpaid_accumulative: isUnpaid,
          // OIL expiry fields (serializer: earliest_oil_expiry_date, oil_expiry_entries)
          earliest_expiry_date: balance.earliest_oil_expiry_date || balance.earliest_expiry_date || null,
          oil_expiry_entries: balance.oil_expiry_entries || null,
        };
      });
      
      return {
        balances: transformedBalances,
        year: new Date().getFullYear()
      };
    },
  });

  // Pending approvals for current user (reporting officer)
  const { data: pendingApprovals = [], isLoading: approvalsLoading } = useQuery({
    // Scoped to user's staff_id to prevent stale data across different sessions
    queryKey: ['pendingApprovals', user?.staff_id],
    staleTime: 0,
    refetchOnMount: 'always', // Ensures data refetches when navigating back to Dashboard
    queryFn: async () => {
      try {
        return await fetchRoPendingApprovals();
      } catch (e) {
        console.error('Error loading pending approvals:', e);
        return [];
      }
    },
    enabled: !!user && isApprover,
    // Items requiring document verification (sick leave, NS, etc.) are shown in the list
    // but rendered as locked in the modal footer ("Awaiting HR verification").
    // Removing them from the list entirely caused the My Approvals card to silently
    // disappear for ROs whose teams primarily submitted doc-required leave types.
    select: (items) => (Array.isArray(items) ? items : []),
  });

  // Visibility guard for My Approvals
  const shouldShowApprovals = (pendingApprovals.length > 0 || isApprover) && !(isAdmin && !isApprover);

  // Deep-link handler: notification / digest click with ?approve=<id> opens the
  // approval modal. LeaveApplication uses an integer PK; the query param is a
  // string — compare loosely or L2 Trade Leaders land on a false "already actioned" toast.
  useEffect(() => {
    const approveId = searchParams.get('approve');
    if (!approveId || !user || approvalsLoading) return;

    // Clean the URL param immediately so a refresh or back-nav doesn't re-trigger
    setSearchParams(prev => { prev.delete('approve'); return prev; }, { replace: true });

    let cancelled = false;
    const openFromInboxOrFetch = async () => {
      const target = pendingApprovals.find((leave) => sameLeaveApplicationId(leave.id, approveId));
      if (target) {
        if (!cancelled) {
          setApprovalDetailsModal({ open: true, leave: target, rejecting: false, rejectionReason: '' });
        }
        return;
      }
      try {
        const res = await staffAPI.getLeaveApplication(approveId);
        const leave = res.data;
        const status = String(leave?.leave_status || leave?.status || '').toLowerCase();
        if (!cancelled && isAcknowledgedNsLeave(leave)) {
          toast.info('You already acknowledged this NS leave. HR still verifies the call-up.');
          return;
        }
        if (!cancelled && leave?.id && (status === 'submitted' || status === 'pending')) {
          setApprovalDetailsModal({ open: true, leave, rejecting: false, rejectionReason: '' });
          return;
        }
      } catch (err) {
        console.error('Deep-link leave fetch failed:', err);
      }
      if (!cancelled) {
        toast.info('This leave request is no longer pending your approval — it may have already been actioned.');
      }
    };

    openFromInboxOrFetch();
    return () => { cancelled = true; };
  }, [searchParams, user, pendingApprovals, approvalsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: pendingDocApprovals, isLoading: pendingDocsLoading } = useQuery({
    queryKey: ['pendingDocApprovalsSummary'],
    queryFn: () => staffAPI.getPendingDocumentApprovalsSummary(),
    enabled: !!user && isAdminAccess,
    // This is an operational inbox shared across HR users. Refetch while the
    // Dashboard is open so uploads from another session surface without a reload.
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    select: (response) => {
      const data = response.data;
      // Defensive filtering: only show true pending-like statuses when available
      // Backend already filters, but this provides extra safety
      const allowedStatuses = new Set(['pending', 'ocr_completed', 'submitted']);
      const filteredRecent = (data?.recent_pending || []).filter(doc => {
        const status = (doc.status || '').toLowerCase();
        // If status present, enforce allowed list; if absent, keep for backward compatibility
        return !status || allowedStatuses.has(status);
      });
      const recentPending = keepLatestIdCardPerSide(filteredRecent);
      return {
        ...data,
        recent_pending: recentPending
      };
    },
  });

  // Fetch leave applications for pending verification items that need date display
  // (medical certificates + flight tickets). Backend limits recent_pending to 50
  // items max, so worst case is 50 API calls. React Query caching prevents refetches.
  const allPendingDocs = pendingDocApprovals?.recent_pending || [];
  const docsNeedingLeaveApp = allPendingDocs
    .map(doc => ({
      ...doc,
      _resolvedAppId: doc.application_id || doc.leave_application_id,
    }))
    .filter(doc =>
      (doc.document_type === 'medical_certificate' || doc.document_type === 'flight_ticket')
      && doc._resolvedAppId
    );

  const leaveAppQueries = useQueries({
    queries: docsNeedingLeaveApp.map((doc) => ({
      queryKey: ['leaveApplication', doc._resolvedAppId],
      queryFn: () => staffAPI.getLeaveApplication(doc._resolvedAppId),
      enabled: !!doc._resolvedAppId && isAdminAccess,
      staleTime: 2 * 60 * 1000,
      retry: 1,
      select: (response) => response.data,
    })),
  });

  const leaveAppMap = React.useMemo(() => {
    const map = new Map();
    leaveAppQueries.forEach((query, index) => {
      const appId = docsNeedingLeaveApp[index]?._resolvedAppId;
      if (query.data && appId) {
        map.set(appId, query.data);
      }
    });
    return map;
  }, [leaveAppQueries, docsNeedingLeaveApp]);

  // RO Team Exceptions - only load if user is a reporting officer
  const { data: roTeamExceptions = [], isLoading: roExceptionsLoading } = useQuery({
    queryKey: ['myTeamExceptions'],
    queryFn: staffAPI.getMyTeamExceptions,
    enabled: !!user?.is_reporting_officer,
    select: (response) => response.data || [],
  });

  // Overlapping team leaves — fetched on-demand when the approval modal opens
  const overlappingLeaveId = approvalDetailsModal.open ? approvalDetailsModal.leave?.id : null;
  const { data: overlappingTeamLeaves = [], isLoading: overlappingLoading } = useQuery({
    queryKey: ['overlappingTeamLeaves', overlappingLeaveId],
    queryFn: () => staffAPI.getOverlappingTeamLeaves(overlappingLeaveId).then(r => r.data || []),
    enabled: !!overlappingLeaveId,
    staleTime: 30_000,
  });

  const handleApprove = async (leaveId) => {
    setApprovalActionLoading(true);
    try {
      const res = await staffAPI.approveLeaveApplication(leaveId);
      const data = res?.data || {};
      if (data.status === 'pending') {
        toast.success(`✅ Approved at level ${(data.current_level ?? 2) - 1} — awaiting next approver`);
      } else {
        toast.success('✅ Leave application approved');
      }
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      return true;
    } catch (error) {
      console.error('Approve failed:', error);
      const status = error?.response?.status;
      const respData = error?.response?.data;
      const detail = respData?.error || respData?.detail
        || (Array.isArray(respData) ? respData.join('; ') : null)
        || (respData?.non_field_errors?.join('; '));
      if (status === 403) {
        toast.error(`❌ ${detail || 'You don\'t have permission to approve this request. Only the assigned approver or admin can approve.'}`);
      } else if (status === 401) {
        toast.error('🔒 Session expired. Please sign in again.');
      } else {
        toast.error(`❌ Failed to approve${detail ? `: ${detail}` : ''}`);
      }
      return false;
    } finally {
      setApprovalActionLoading(false);
    }
  };

  const handleReject = async (leaveId, reason) => {
    setApprovalActionLoading(true);
    try {
      await staffAPI.rejectLeaveApplication(leaveId, { reason: reason || 'Rejected by reporting officer' });
      toast.success('✅ Leave application rejected');
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      return true;
    } catch (error) {
      console.error('Reject failed:', error);
      toast.error('❌ Failed to reject');
      return false;
    } finally {
      setApprovalActionLoading(false);
    }
  };

  const handleAcknowledge = async (leaveId) => {
    setApprovalActionLoading(true);
    try {
      await staffAPI.acknowledgeNSLeave(leaveId);
      toast.success('NS leave acknowledged. HR still verifies the call-up.');
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      setApprovalDetailsModal({ open: false, leave: null, rejecting: false, rejectionReason: '' });
      return true;
    } catch (error) {
      console.error('Acknowledge failed:', error);
      const detail = error?.response?.data?.error || error?.response?.data?.detail;
      if (error?.response?.status === 409) {
        toast.info('Already acknowledged. HR still verifies the call-up.');
        queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
        setApprovalDetailsModal({ open: false, leave: null, rejecting: false, rejectionReason: '' });
        return true;
      }
      toast.error(`Failed to acknowledge${detail ? `: ${detail}` : ''}`);
      return false;
    } finally {
      setApprovalActionLoading(false);
    }
  };

  const handleOpenVerificationModal = async (document) => {
    if (!document.id) {
      console.error('Cannot open verification modal: missing document ID');
      toast.error('Cannot open document verification: missing document ID');
      return;
    }
    
    const docEmployeeName = document.employee_name || null;
    const docStaffCode = document.staff_id || document.employee_staff_id || null;

    // Special handling for NS Call-up Order documents
    if (document.document_type === 'ns_call_up') {
      setNsVerificationModal({
        open: true,
        documentApprovalId: document.id,
        leaveApplicationId: document.application_id || document.leave_application_id || null,
        employeeName: docEmployeeName,
        employeeStaffCode: docStaffCode,
      });
      return;
    }

    // Special handling for Medical Certificate documents → dedicated MC modal
    if (document.document_type === 'medical_certificate') {
      const appId = document.application_id || document.orphaned_leave_application_id || null;
      if (appId) {
        const application = leaveAppMap?.get(appId) || null;
        setMcVerificationModal({ open: true, applicationId: appId, application });
        return;
      }
    }

    // Flight ticket → fetch leave application then open dedicated modal
    if (document.document_type === 'flight_ticket') {
      const leaveAppId = document.leave_application_id || document.application_id;
      if (!leaveAppId) {
        toast.error('Cannot verify: flight ticket not linked to a leave application');
        return;
      }
      try {
        const resp = await staffAPI.getLeaveApplicationAdminDetail(leaveAppId);
        setFlightTicketModal({
          open: true,
          documentApprovalId: document.id,
          leaveApplication: resp.data,
        });
      } catch {
        toast.error('Failed to load leave application details');
      }
      return;
    }
    
    setVerificationModal({
      open: true,
      documentApprovalId: document.id,
      documentType: document.document_type,
      applicationId: document.application_id || null,
      employeeName: docEmployeeName,
      employeeStaffCode: docStaffCode,
    });
  };

  const handleVerificationComplete = (result) => {
    const verifiedDocumentType = verificationModal.documentType;
    setVerificationModal({ open: false, documentApprovalId: null, documentType: null });
    if (result && result.success) {
      if (result.consumption_split?.items?.length > 1) {
        const parts = result.consumption_split.items
          .map(i => `${i.days} day(s) ${i.leave_type}`)
          .join(', ');
        toast.success(`${result.message} (${parts})`, { duration: 6000 });
      } else {
        toast.success(result.message);
      }
      queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
      if (['education_certificate', 'professional_certificate'].includes(verifiedDocumentType)) {
        // Backend signal refreshes classification eligibility; invalidate the whole
        // Skills Upgrading tree so missing-certs / pathways / levy queue refetch.
        queryClient.invalidateQueries({ queryKey: ['skills-upgrading'] });
        queryClient.invalidateQueries({ queryKey: ['timeline', 'certificates'] });
        queryClient.invalidateQueries({ queryKey: ['timeline', 'education'] });
      }
    }
  };

  // Deep-link handler: notification click with ?document_id=<id> auto-opens the
  // matching document verification modal (MC / NS / flight ticket / generic).
  useEffect(() => {
    const documentId = searchParams.get('document_id');
    if (!documentId || !user || !isAdminAccess || pendingDocsLoading) return;

    // Clean the URL params immediately so a refresh or back-nav doesn't re-trigger
    setSearchParams(prev => { prev.delete('document_id'); prev.delete('type'); return prev; }, { replace: true });

    const target = (pendingDocApprovals?.recent_pending || [])
      .find(d => String(d.id) === String(documentId));
    if (target) {
      handleOpenVerificationModal(target);
    } else {
      toast.info('This document is no longer pending verification — it may have already been processed.', {
        icon: 'ℹ️',
        duration: 5000,
      });
    }
  }, [searchParams, user, isAdminAccess, pendingDocsLoading, pendingDocApprovals]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNSVerificationComplete = () => {
    setNsVerificationModal({ open: false, documentApprovalId: null, leaveApplicationId: null });
    // Refresh the pending documents data
    queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
    // Also refresh pending approvals to show updated status
    queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
    // Refresh leave applications
    queryClient.invalidateQueries({ queryKey: ['leave'] });
  };

  const handleMCVerificationComplete = () => {
    setMcVerificationModal({ open: false, applicationId: null, application: null });
    // Refresh the pending documents data
    queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
    // Also refresh pending approvals to show updated status
    queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
    // Refresh leave applications
    queryClient.invalidateQueries({ queryKey: ['leave'] });
  };

  // Admin Dashboard Document Verification KPI lands here via #pending-verification.
  useEffect(() => {
    if (!isAdminAccess || window.location.hash !== '#pending-verification') return;
    const node = document.getElementById('pending-verification');
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isAdminAccess, pendingDocsLoading]);

  const handleOpenExceptionModal = (exception) => {
    setExceptionModal({ open: true, exception });
  };

  const handleExceptionResolved = () => {
    // Refresh team exceptions data
    queryClient.invalidateQueries({ queryKey: ['myTeamExceptions'] });
    // Also invalidate admin exceptions list (used in AttendanceManagement)
    // Query key pattern: ['attendance', 'exceptions', params]
    // Use predicate to match all exception queries regardless of params
    queryClient.invalidateQueries({ 
      predicate: (query) => 
        query.queryKey[0] === 'attendance' && 
        query.queryKey[1] === 'exceptions'
    });
  };

  // Helper function to determine if leave type requires per-application document verification
  // PRIORITY ORDER: Use proper database fields, fall back to string matching if needed
  const requiresDocumentVerification = (leave) => {
    const leaveTypeNameEarly = leave.leave_type?.leave_type_name || leave.leave_type_name || leave.leave_type || '';
    const typeLowerEarly = String(leaveTypeNameEarly).toLowerCase();
    // BUSINESS RULE: RO approves these directly; document submission by employee is a separate deferred step.
    // Childcare Leave: initial HR verification happens once at entitlement grant, not per application.
    // Compassionate Leave: death certificate is optional at submission; employee has a grace period to upload.
    // Paternity / Shared Parental Leave: entitlement is activated by approved supporting docs
    // (birth certificate or interim birth notification), so RO should still approve each leave application.
    // Marriage Leave: marriage certificate is verified by HR once at entitlement grant (the employee
    // can only apply after that approval), so RO should still approve each leave application.
    if (
      typeLowerEarly.includes('childcare') ||
      typeLowerEarly.includes('compassionate') ||
      typeLowerEarly.includes('paternity') ||
      typeLowerEarly.includes('shared parental') ||
      typeLowerEarly.includes('marriage')
    ) {
      return false;
    }

    // METHOD 1: Check leave type object directly (PROPER WAY)
    if (leave.leave_type) {
      const leaveType = leave.leave_type;

      // Check if medical certificate is required (primary field)
      if (leaveType.leave_type_requires_medical_cert) {
        return true;
      }

      // Check if trigger event requires documents (primary field)
      if (leaveType.leave_type_trigger_event && leaveType.leave_type_trigger_event !== 'none') {
        return true;
      }
    }

    // FALLBACK: Handle different data structures (backward compatibility)
    const requiresMC = leave.requires_mc || leave.mc_required;
    const triggerEvent = leave.trigger_event;

    if (requiresMC) return true;
    if (triggerEvent && triggerEvent !== 'none') return true;

    // LAST RESORT: String matching for edge cases (should be avoided in production)
    const leaveTypeName = leaveTypeNameEarly;
    const typeLower = typeLowerEarly;

    // Only check for known problematic cases that might not have proper fields
    const docRequiredTypes = ['sick', 'medical', 'outpatient', 'hospitalisation', 'maternity', 'paternity'];
    return docRequiredTypes.some(docType => typeLower.includes(docType));
  };

  // Helper function to humanize document type labels
  const humanizeDocumentType = (documentType) => {
    if (!documentType) return 'Document';
    
    // Special handling for ID cards
    if (documentType === 'id_card_front') return 'ID Card (Front)';
    if (documentType === 'id_card_back') return 'ID Card (Back)';
    if (documentType === 'ns_call_up') return 'NS Call-up Order';
    if (documentType === 'driving_license') return 'Driving Licence';
    
    // Replace underscores with spaces and split into words
    const words = documentType.replace(/_/g, ' ').split(' ');
    
    // Capitalize each word properly
    return words.map(word => {
      // Special handling for acronyms and common terms
      if (word.toLowerCase() === 'id') return 'ID';
      if (word.toLowerCase() === 'mc') return 'MC';
      // Capitalize first letter of each word
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // Renders the type-specific content section inside the RO approval modal.
  // Keyed by the section identifiers declared in LEAVE_TYPE_CONFIG.
  const renderLeaveSection = (sectionKey, leave) => {
    switch (sectionKey) {

      case 'oil_claim':
        return (
          <div key="oil_claim" className="space-y-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-blue-800">Off in Lieu Claim Details</h5>
            <div>
              <label className="text-sm font-medium text-gray-700">Work Description</label>
              <div className="mt-1">
                <p className="text-sm text-gray-900">
                  {leave.oil_work_description || leave.work_description || leave.leave_reason || 'No description provided'}
                </p>
              </div>
            </div>
            {(leave.oil_project_code || leave.project_code) && (
              <div>
                <label className="text-sm font-medium text-gray-700">Project Code</label>
                <div className="mt-1">
                  <p className="text-sm text-gray-900">{leave.oil_project_code || leave.project_code}</p>
                </div>
              </div>
            )}
            {leave.oil_dates && Array.isArray(leave.oil_dates) && leave.oil_dates.length > 0 && (
              <OilClaimWorkDates oilDates={leave.oil_dates} />
            )}
          </div>
        );

      case 'oil_usage':
        return (
          <div key="oil_usage">
            <label className="text-sm font-medium text-gray-700">Reason</label>
            <div className="mt-1">
              <p className="text-sm text-gray-900">{leave.leave_reason || 'No reason provided'}</p>
            </div>
          </div>
        );

      case 'wfh_reason':
        return (leave.wfh_reason || leave.leave_reason) ? (
          <div key="wfh_reason">
            <label className="text-sm font-medium text-gray-700">Reason for WFH</label>
            <div className="mt-1">
              <p className="text-sm text-gray-900">
                {getWFHReasonLabel(leave.wfh_reason, leave.wfh_other_reason) || leave.leave_reason || 'No reason provided'}
              </p>
            </div>
          </div>
        ) : null;

      case 'compassionate':
        return (
          <div key="compassionate" className="space-y-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-purple-800">Compassionate Leave Details</h5>
            {leave.compassionate_relationship && (
              <div>
                <label className="text-sm font-medium text-gray-700">Relationship to Deceased</label>
                <div className="mt-1">
                  <p className="text-sm text-gray-900 capitalize">{leave.compassionate_relationship.replace(/_/g, ' ')}</p>
                </div>
              </div>
            )}
            {leave.deceased_name && (
              <div>
                <label className="text-sm font-medium text-gray-700">Name of Deceased</label>
                <div className="mt-1"><p className="text-sm text-gray-900">{leave.deceased_name}</p></div>
              </div>
            )}
            {leave.leave_reason && (
              <div>
                <label className="text-sm font-medium text-gray-700">Additional Details</label>
                <div className="mt-1"><p className="text-sm text-gray-900">{leave.leave_reason}</p></div>
              </div>
            )}
          </div>
        );

      case 'sick_info':
        return (
          <div key="sick_info" className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
            <h5 className="text-sm font-semibold text-orange-800">Medical Certificate Status</h5>
            <div className="flex items-center gap-2">
              {leave.has_medical_certificate ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-800">MC submitted — pending HR verification</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <span className="text-sm text-gray-800">MC not yet uploaded by employee</span>
                </>
              )}
            </div>
          </div>
        );

      case 'on_course':
        return (
          <div key="on_course" className="space-y-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-blue-800">Course / Training Details</h5>
            {leave.course_name && (
              <div>
                <label className="text-sm font-medium text-gray-700">Course Name</label>
                <div className="mt-1"><p className="text-sm text-gray-900">{leave.course_name}</p></div>
              </div>
            )}
            {leave.course_location && (
              <div>
                <label className="text-sm font-medium text-gray-700">Location</label>
                <div className="mt-1"><p className="text-sm text-gray-900">{leave.course_location}</p></div>
              </div>
            )}
            {leave.leave_reason && (
              <div>
                <label className="text-sm font-medium text-gray-700">Additional Details</label>
                <div className="mt-1"><p className="text-sm text-gray-900">{leave.leave_reason}</p></div>
              </div>
            )}
          </div>
        );

      case 'ns_details':
        return (
          <div key="ns_details" className="space-y-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
              <Shield className="w-4 h-4" />
              NS Call-up Details
            </h5>
            {leave.activity_type && (
              <div>
                <label className="text-xs font-medium text-gray-500">Activity Type</label>
                <p className="text-sm text-gray-900">{leave.activity_type}</p>
              </div>
            )}
            {leave.leave_reason && leave.leave_reason !== 'National Service Leave' && (
              <div>
                <label className="text-xs font-medium text-gray-500">Reason</label>
                <p className="text-sm text-gray-900">{leave.leave_reason}</p>
              </div>
            )}
          </div>
        );

      case 'reason':
      default:
        return (
          <div key="reason">
            <label className="text-sm font-medium text-gray-700">Reason</label>
            <div className="mt-1">
              <p className="text-sm text-gray-900">{leave.leave_reason || 'No reason provided'}</p>
            </div>
          </div>
        );
    }
  };

  // Metrics removed per design preference - dashboard focuses on actionable items instead

  if (userLoading) {
    return (
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Cards Grid Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border">
              {/* Card Header Skeleton */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-48 animate-pulse"></div>
                  </div>
                </div>
              </div>
              {/* Card Content Skeleton */}
              <div className="p-4 space-y-3">
                {[1, 2, 3].map(j => (
                  <div key={j} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
      {/* Page Header — Compact Page Header (employee) */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-5 h-5 text-blue-800" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {displayName}!
          </h1>
        </div>
        <p className="text-sm text-gray-500 ml-12">Your overview for today</p>
      </div>



      {/* Key Metrics removed per design preference */}

      {/* Nightwork Dashboard Card */}
      <NightworkDashboardCard />

      {/* Passport custody actions */}
      <PassportDashboardCard user={user} />

      <WorkPassDashboardCard user={user} />
      <ResignationNoticeDashboardCard user={user} />

      {/* Worker Trade Transfer Dashboard Card */}
      <TradeTransferDashboardCard />

             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Approvals — rendered first in the DOM so it leads both the stacked
            single-column mobile layout and the keyboard/screen-reader reading
            order. Acting on this queue is the approver's primary task, so it is
            the first card they encounter on every breakpoint.
            Visible whenever the user has RO/manager responsibilities; shows an
            empty state when the queue is clear rather than disappearing entirely,
            so ROs always know the section exists and is actively monitored. */}
        {shouldShowApprovals && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-teal-700" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-subsection-header truncate">My Approvals</h2>
                    <p className="text-body mt-1 truncate">Leave requests awaiting your action</p>
                  </div>
                </div>
                {pendingApprovals.length > 0 && (
                  <CountBadge count={pendingApprovals.length} theme="warning" size="md" className="ml-2 flex-shrink-0" />
                )}
              </div>
            </div>
            <div className="card-content">
              {approvalsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <LoadingSpinner />
                </div>
              ) : pendingApprovals.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">All caught up</p>
                  <p className="text-xs text-gray-500 mt-1">No pending leave approvals</p>
                </div>
              ) : (
                <div className={`space-y-3 ${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} overflow-y-auto`}>
                  {pendingApprovals.map((leave) => (
                    <div key={leave.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border hover:border-blue-200 transition-colors">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-4 h-4 text-blue-800" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate flex items-center gap-1">
                            <span className="truncate">{(() => {
                              const applied = leave.leave_type?.leave_type_name || leave.leave_type_name;
                              const p = leave.leave_consumption_proposal;
                              if (p?.items?.length === 1 && p.items[0].leave_type !== applied) return p.items[0].leave_type;
                              return applied;
                            })()}</span>
                            {(() => {
                              const ltName = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
                              const isNS = ltName.includes('national service');
                              if (isNS) {
                                const status = (leave.leave_status || '').toLowerCase();
                                if (status === 'approved') {
                                  return <span className="flex-shrink-0"><StatusBadge status="approved" label="HR Verified" /></span>;
                                }
                                if (status === 'rejected') {
                                  return <span className="flex-shrink-0"><StatusBadge status="rejected" label="HR Rejected" /></span>;
                                }
                                return <span className="flex-shrink-0"><Shield className="w-3.5 h-3.5 text-blue-600" /></span>;
                              }
                              if (requiresDocumentVerification(leave)) {
                                return <span className="flex-shrink-0"><InfoBadge color="orange" label="HR" /></span>;
                              }
                              return null;
                            })()}
                          </p>
                          <p className="text-xs text-gray-600 truncate">{leave.staff_name || leave.employee_name}</p>
                          {(() => {
                            const s = leave.leave_start_date || leave.start_date;
                            const e = leave.leave_end_date || leave.end_date;
                            const same = s && e && (new Date(s)).toDateString() === (new Date(e)).toDateString();
                            const sp = leave.leave_start_time_period;
                            const ep = leave.leave_end_time_period;
                            const displayChips = getLeavePeriodDisplayChips(leave);
                            const fullSame = same && !displayChips.start && !displayChips.end && ((sp || 'AM') === 'AM') && ((ep || 'PM') === 'PM');
                            const startYear = getYearInSingapore(s);
                            const endYear = getYearInSingapore(e);
                            const sameYear = startYear === endYear;
                            return (
                              <p className="text-xs flex items-center gap-1">
                                <span>{same ? formatDate(s) : (sameYear ? formatDateShort(s) : formatDate(s))}</span>
                                {same ? (
                                  fullSame ? null : (
                                    <>
                                      <SessionPeriodChip session={displayChips.start} />
                                      <SessionPeriodChip session={displayChips.end} />
                                    </>
                                  )
                                ) : (
                                  <>
                                    <SessionPeriodChip session={displayChips.start} />
                                    <span>—</span>
                                    <span>{formatDate(e)}</span>
                                    <SessionPeriodChip session={displayChips.end} />
                                  </>
                                )}
                              </p>
                            );
                          })()}
                          {/* Display leave days */}
                          <p className="text-xs text-blue-600 font-medium">
                            {(() => {
                              const days = getDisplayTotalDays(leave);
                              return `${formatLeaveDays(days)} ${pluralizeDays(days)}`;
                            })()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Eye button to view details and approve/reject */}
                        <button
                          type="button"
                          className="icon-button flex-shrink-0"
                          title="View details"
                          aria-label="View details"
                          onClick={() => setApprovalDetailsModal({ open: true, leave, rejecting: false, rejectionReason: '' })}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* RO Team Exceptions (Reporting Officers Only) */}
        {user?.is_reporting_officer && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-subsection-header truncate">Team Alerts</h2>
                    <p className="text-body mt-1 truncate">Missing attendance data for your team</p>
                  </div>
                </div>
                {roTeamExceptions.length > 0 && (
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-100 text-orange-600 text-xs font-bold rounded-full border border-orange-200 flex-shrink-0">
                    {roTeamExceptions.length}
                  </span>
                )}
              </div>
            </div>
            <div className="card-content">
              {roExceptionsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : roTeamExceptions.length > 0 ? (
                <div className={`space-y-3 ${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} overflow-y-auto`}>
                  {roTeamExceptions.map((exception) => {
                    const exceptionType = exception.type || exception.exception_type || '';
                    const formatExceptionType = (type) => {
                      if (!type) return 'Unknown';
                      const typeMap = {
                        'absent_no_leave': 'Absent',
                        'missing_clockout': 'Missing Clock-Out',
                        'late_arrival': 'Late Arrival',
                        'early_departure': 'Early Departure',
                        'geofence_violation': 'Geofence Violation'
                      };
                      return typeMap[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                    };
                    
                    return (
                      <div key={exception.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border hover:border-orange-200 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">
                            {exception.employee_name || exception.staff_name || exception.employee_full_name}
                          </div>
                          <div className="text-xs text-gray-600 flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              exceptionType === 'absent_no_leave' ? 'bg-red-100 text-red-800' :
                              exceptionType === 'missing_clockout' ? 'bg-yellow-100 text-yellow-800' :
                              exceptionType === 'late_arrival' ? 'bg-yellow-100 text-yellow-800' :
                              exceptionType === 'early_departure' ? 'bg-orange-100 text-orange-800' :
                              exceptionType === 'geofence_violation' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {formatExceptionType(exceptionType)}
                            </span>
                            <span>•</span>
                            <span>{formatDate(exception.date || exception.exception_date)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpenExceptionModal(exception)}
                          className="icon-button flex-shrink-0"
                          title="Review exception"
                          aria-label="Review exception"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} flex items-center justify-center`}>
                  <div className="text-center py-8 text-gray-500">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-card-header text-gray-600">No pending exceptions</p>
                    <p className="text-small">Your team's attendance exceptions will appear here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pending Verification (Admin Only) */}
        {isAdminAccess && (
          <div id="pending-verification" className="card">
            <div className="card-header">
                                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <FileText className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h2 className="text-subsection-header">Pending HR Verification</h2>
                        <p className="text-body mt-1">Documents requiring an HR decision</p>
                      </div>
                    </div>
                    {pendingDocApprovals?.total_pending_documents > 0 && (
                      <CountBadge
                        count={pendingDocApprovals.total_pending_documents}
                        theme="admin"
                        size="md"
                        className="ml-2 flex-shrink-0"
                      />
                    )}
                  </div>
            </div>
          <div className="card-content">
            {pendingDocsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (() => {
              // Backend already excludes HR admin's own documents and terminal
              // leave (draft / cancelled / rejected). Defensive leave-status
              // filter covers a same-session cancel before the summary refetch.
              const pendingDocs = (pendingDocApprovals?.recent_pending || []).filter((doc) => {
                const docAppId = doc.application_id || doc.leave_application_id;
                if (!docAppId) return true;
                const leaveApp = leaveAppMap.get(docAppId);
                if (!leaveApp) return true;
                const leaveStatus = (leaveApp.leave_status || leaveApp.status || '').toLowerCase();
                return !['cancelled', 'rejected', 'draft'].includes(leaveStatus);
              });
              
              return (pendingDocApprovals?.total_pending_documents > 0 || pendingDocs.length > 0) ? (
                <div className={`space-y-2 ${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} overflow-y-auto`}>
                  {pendingDocs.map((doc, index) => {
                      const docAppId = doc.application_id || doc.leave_application_id;
                      const leaveApp = (doc.document_type === 'medical_certificate' || doc.document_type === 'flight_ticket')
                        && docAppId
                        ? leaveAppMap.get(docAppId)
                        : null;
                      
                      // No per-row status pill. This card is already the pending
                      // inbox — pending / ocr_completed / submitted are the same
                      // HR job (open and verify). Pipeline labels like
                      // "Unverified" vs "Ready to verify" compete without changing
                      // the action.
                      return (
                        <div key={doc.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900">
                              {doc.employee_name && doc.staff_id 
                                ? `${doc.employee_name} · ${doc.staff_id}`
                                : doc.employee_name || doc.staff_id}
                            </div>
                            <div className="text-xs text-gray-600">
                              {humanizeDocumentType(doc.document_type)}
                            </div>
                            {/* Show leave dates for medical certificates */}
                            {doc.document_type === 'medical_certificate' && leaveApp && (() => {
                              const s = leaveApp.leave_start_date || leaveApp.start_date;
                              const e = leaveApp.leave_end_date || leaveApp.end_date;
                              if (!s) return null;
                              
                              const same = s && e && (new Date(s)).toDateString() === (new Date(e)).toDateString();
                              const sp = leaveApp.leave_start_time_period;
                              const ep = leaveApp.leave_end_time_period;
                              const displayChips = getLeavePeriodDisplayChips(leaveApp);
                              const fullSame = same && !displayChips.start && !displayChips.end && ((sp || 'AM') === 'AM') && ((ep || 'PM') === 'PM');
                              
                              return (
                                <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                  <span>{formatDate(s)}</span>
                                  {same ? (
                                    fullSame ? null : (
                                      <>
                                        <SessionPeriodChip session={displayChips.start} />
                                        <SessionPeriodChip session={displayChips.end} />
                                      </>
                                    )
                                  ) : (
                                    <>
                                      <SessionPeriodChip session={displayChips.start} />
                                      <span>-</span>
                                      <span>{formatDate(e)}</span>
                                      <SessionPeriodChip session={displayChips.end} />
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Show leave period for flight tickets */}
                            {doc.document_type === 'flight_ticket' && leaveApp && (() => {
                              const s = leaveApp.leave_start_date || leaveApp.start_date;
                              const e = leaveApp.leave_end_date || leaveApp.end_date;
                              if (!s) return null;
                              const same = s && e && (new Date(s)).toDateString() === (new Date(e)).toDateString();
                              return (
                                <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                  <span>{formatDate(s)}</span>
                                  {!same && e && (
                                    <>
                                      <span>-</span>
                                      <span>{formatDate(e)}</span>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <IconButton
                              type="button"
                              icon={Eye}
                              onClick={() => handleOpenVerificationModal(doc)}
                              variant="primary"
                              title="Review document"
                              ariaLabel={`Review ${humanizeDocumentType(doc.document_type)} for ${doc.employee_name || doc.staff_id || 'employee'}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                  <div className={`${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} flex items-center justify-center`}>
                    <div className="text-center py-8 text-gray-500">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-card-header text-gray-600">No pending documents</p>
                      <p className="text-small">Documents awaiting verification will appear here</p>
                    </div>
                  </div>
                );
            })()}
              </div>
          </div>
        )}

        {/* Recent Leave Applications */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-800" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-subsection-header truncate">Recent Leave Applications</h2>
                  <p className="text-body mt-1 truncate">Your latest requests</p>
                </div>
              </div>
              <Link
                to="/leave"
                className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 flex-shrink-0"
                title="View all leave applications"
              >
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="card-content">
            {leaveLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : leaveError ? (
              <div className={`${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} flex items-center justify-center`}>
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <p className="text-card-header text-gray-600">Could not load leave applications</p>
                  <p className="text-small">Please refresh the page or try again later</p>
                </div>
              </div>
            ) : leaveApplications?.length > 0 ? (
              <div className={`space-y-4 ${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} overflow-y-auto`}>
                {leaveApplications.map((leave) => {
                  const appliedTypeName = leave.leave_type?.leave_type_name || leave.leave_type_id?.leave_type_name || leave.leave_type_name || 'Leave';
                  const proposal = leave.leave_consumption_proposal;
                  const leaveTypeName = (proposal?.items?.length === 1 && proposal.items[0].leave_type !== appliedTypeName)
                    ? proposal.items[0].leave_type : appliedTypeName;
                  const start = leave.leave_start_date || leave.start_date;
                  const end = leave.leave_end_date || leave.end_date;
                  const status = resolveLeaveStatus(leave);
                  const sameDay = start && end && (new Date(start)).toDateString() === (new Date(end)).toDateString();
                  const startPeriod = leave.leave_start_time_period;
                  const endPeriod = leave.leave_end_time_period;
                  const displayChips = getLeavePeriodDisplayChips(leave);
                  const isFullDaySame = sameDay && ((startPeriod || 'AM') === 'AM') && ((endPeriod || 'PM') === 'PM');

                  // Get appropriate icon and color based on leave type
                  // Colors aligned with admin calendar (/admin/leave?tab=calendar)
                  const getLeaveTypeIcon = (leaveTypeName) => {
                    const name = leaveTypeName?.toLowerCase() || '';
                    if (name.includes('urgent leave')) return { icon: AlertTriangle, color: 'orange' };
                    if (name.includes('home leave') || name.includes('home-leave')) return { icon: Plane, color: 'sky' };
                    if (name.includes('annual') || name.includes('vacation')) return { icon: Sun, color: 'blue' };
                    if (name.includes('outpatient') || (name.includes('sick') && !name.includes('hospitalisation'))) return { icon: Thermometer, color: 'amber' };
                    if (name.includes('hospitalisation')) return { icon: Hospital, color: 'red' };
                    if (name.includes('maternity')) return { icon: Baby, color: 'pink' };
                    if (name.includes('paternity')) return { icon: Baby, color: 'purple' };
                    if (name.includes('shared parental')) return { icon: Baby, color: 'fuchsia' };
                    if (name.includes('extended') && name.includes('childcare')) return { icon: Baby, color: 'emerald' };
                    if (name.includes('childcare')) return { icon: Baby, color: 'green' };
                    if (name.includes('marriage')) return { icon: Gem, color: 'rose' };
                    if (name.includes('compassionate') || name.includes('bereavement')) return { icon: Heart, color: 'gray' };
                    if (name.includes('unpaid')) return { icon: CalendarX, color: 'slate' };
                    if (name === 'off in lieu' || name.includes('oil')) return { icon: Hourglass, color: 'teal' };
                    if (name.includes('external activity') || name.includes('external')) return { icon: MapPinned, color: 'cyan' };
                    if (name.includes('work from home') || name.includes('wfh')) return { icon: Home, color: 'indigo' };
                    if (name === 'on course') return { icon: GraduationCap, color: 'violet' };
                    if (name.includes('national service')) return { icon: Shield, color: 'lime' };
                    return { icon: CalendarDays, color: 'stone' };
                  };

                  const { icon: LeaveIcon, color } = getLeaveTypeIcon(leaveTypeName);

                  // Handle status label (matching Leave.js logic for auto_approved and sick leave verification)
                  const isSickLeave = leaveTypeName.toLowerCase().includes('outpatient') && leaveTypeName.toLowerCase().includes('sick') || leaveTypeName.toLowerCase().includes('hospitalisation');
                  const statusLabel = (isSickLeave && status === 'approved') ? 'Verified' : 
                                    (status === 'auto_approved') ? 'Auto Approved' : 
                                    status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
                  return (
                    <div key={leave.id} className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-lg border hover:border-blue-200 transition-colors">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className={`w-8 h-8 flex-shrink-0 ${color === 'amber' ? 'bg-amber-100' : color === 'blue' ? 'bg-blue-100' : color === 'red' ? 'bg-red-100' : color === 'pink' ? 'bg-pink-100' : color === 'purple' ? 'bg-purple-100' : color === 'orange' ? 'bg-orange-100' : color === 'green' ? 'bg-green-100' : color === 'cyan' ? 'bg-cyan-100' : color === 'teal' ? 'bg-teal-100' : color === 'slate' ? 'bg-slate-100' : color === 'indigo' ? 'bg-indigo-100' : color === 'rose' ? 'bg-rose-100' : color === 'sky' ? 'bg-sky-100' : color === 'fuchsia' ? 'bg-fuchsia-100' : color === 'emerald' ? 'bg-emerald-100' : color === 'violet' ? 'bg-violet-100' : color === 'lime' ? 'bg-lime-100' : color === 'stone' ? 'bg-stone-100' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                          <LeaveIcon className={`w-4 h-4 ${color === 'amber' ? 'text-amber-600' : color === 'blue' ? 'text-blue-800' : color === 'red' ? 'text-red-600' : color === 'pink' ? 'text-pink-600' : color === 'purple' ? 'text-purple-600' : color === 'orange' ? 'text-orange-600' : color === 'green' ? 'text-green-600' : color === 'cyan' ? 'text-cyan-600' : color === 'teal' ? 'text-teal-600' : color === 'slate' ? 'text-slate-600' : color === 'indigo' ? 'text-indigo-600' : color === 'rose' ? 'text-rose-600' : color === 'sky' ? 'text-sky-600' : color === 'fuchsia' ? 'text-fuchsia-600' : color === 'emerald' ? 'text-emerald-600' : color === 'violet' ? 'text-violet-600' : color === 'lime' ? 'text-lime-600' : color === 'stone' ? 'text-stone-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-card-header">{leaveTypeName}</p>
                          <p className="text-small flex items-center gap-1">
                            {(() => {
                              const startYear = getYearInSingapore(start);
                              const endYear = getYearInSingapore(end);
                              const sameYear = startYear === endYear;
                              return <span>{start ? (sameDay ? formatDate(start) : (sameYear ? formatDateShort(start) : formatDate(start))) : 'N/A'}</span>;
                            })()}
                            {sameDay ? (
                              isFullDaySame ? null : (
                                <>
                                  <SessionPeriodChip session={displayChips.start} />
                                  <SessionPeriodChip session={displayChips.end} />
                                </>
                              )
                            ) : (
                              <>
                                <SessionPeriodChip session={displayChips.start} />
                                <span>—</span>
                                <span>{end ? formatDate(end) : 'N/A'}</span>
                                <SessionPeriodChip session={displayChips.end} />
                              </>
                            )}
                          </p>
                          {/* Display leave days */}
                          <p className="text-xs text-blue-600 font-medium">
                            {(() => {
                              const days = getDisplayTotalDays(leave);
                              return `${formatLeaveDays(days)} ${pluralizeDays(days)}`;
                            })()}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <StatusBadge status={status} label={statusLabel} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={`${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} flex items-center justify-center`}>
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-card-header text-gray-600">No recent leave applications</p>
                  <p className="text-small">Your leave requests will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Leave Balances - Enhanced UX */}
        {/*
          FRONTLOAD PREVENTION: Family leave types (Childcare Leave, Maternity Leave, Paternity Leave)
          are only shown after birth certificate approval. This prevents employees from seeing
          leave entitlements before proper documentation is verified.

          DOCUMENT VERIFICATION ENFORCEMENT: Sick leave types (Outpatient Sick Leave, Hospitalisation Leave)
          require medical certificate verification before HR can approve/reject the leave request.
        */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-5 h-5 text-blue-800" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-subsection-header truncate">Leave Balances</h2>
                  <p className="text-body mt-1 truncate">Your available leave days</p>
                </div>
              </div>
              <Link
                to="/leave"
                className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 flex-shrink-0"
                title="View all leave balances"
              >
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="card-content">
            {balancesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : leaveBalances?.balances?.length > 0 ? (
              (() => {
                const oslBalance = leaveBalances.balances.find(b => b.leave_type === 'Outpatient Sick Leave');
                const hlBalance = leaveBalances.balances.find(b => b.leave_type === 'Hospitalisation Leave');
                const ptUser = user?.employment_type === 'part_time';
                const medicalLeavePool = {
                  total: ptUser
                    ? (parseFloat(oslBalance?.accrued_days) || 0) + (parseFloat(hlBalance?.accrued_days) || 0)
                    : (parseFloat(oslBalance?.entitled_days) || 0) + (parseFloat(hlBalance?.entitled_days) || 0),
                  used: (parseFloat(oslBalance?.used_days) || 0) + (parseFloat(hlBalance?.used_days) || 0),
                };
                medicalLeavePool.remaining = Math.max(0, medicalLeavePool.total - medicalLeavePool.used);

                return (
              <div className={`space-y-4 ${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} overflow-y-auto`}>
                {leaveBalances.balances.map((balance, index) => {
                    // Handle special leave types (WFH, External Activity) and Unpaid Leave (accumulative)
                    const isUnpaid = balance.leave_type === 'Unpaid Leave' || balance.is_unpaid_accumulative;
                    const isSpecialType = balance.is_work_arrangement || balance.is_status_only || isUnpaid;
                    
                    // Part-time detection: all leave types use accrual-based display
                    const isPartTime = user?.employment_type === 'part_time';
                    const isAccrualBased = balance.leave_type === 'Annual Leave' || isPartTime;

                    let remaining, total, used, usagePercentage, remainingPercentage;
                    
                    if (isSpecialType) {
                      // For special and Unpaid Leave, show usage count instead of balance
                      const usageCount = balance.usage_count || balance.used_days || 0;
                      remaining = null; // No balance concept
                      total = null; // No total concept
                      used = usageCount;
                      usagePercentage = 0; // No percentage for unlimited types
                      remainingPercentage = 0;
                    } else {
                      // Regular leave types with balance - USE DATABASE VALUES
                      used = balance.used_days || 0; // Use database value, not calculated
                      
                if (isAccrualBased) {
                  // Accrual-based: AL for everyone, ALL types for part-timers
                  remaining = Number(balance.remaining_days ?? 0);
                  total = Number(balance.total_available_days ?? balance.entitled_days ?? 0);
                  usagePercentage = total > 0 ? Math.round((balance.used_days / total) * 100) : 0;
                  remainingPercentage = total > 0 ? Math.round((remaining / total) * 100) : 0;
                } else {
                  // Other leave types use traditional logic
                  remaining = balance.remaining_days || 0;
                  // Use total_available_days (backend handles carry-forward logic correctly)
                  total = Number(balance.total_available_days ?? balance.entitled_days ?? 0);
                  usagePercentage = total > 0 ? Math.round((used / total) * 100) : 0;
                  remainingPercentage = total > 0 ? Math.round((remaining / total) * 100) : 0;
                }
                    }
                    
                    // Get appropriate icon and color based on leave type
                    // Colors aligned with admin calendar (/admin/leave?tab=calendar)
                    const getLeaveTypeIcon = (leaveTypeName) => {
                      const name = leaveTypeName?.toLowerCase() || '';
                      if (name.includes('urgent leave')) return { icon: AlertTriangle, color: 'orange' };
                      if (name.includes('home leave') || name.includes('home-leave')) return { icon: Plane, color: 'sky' };
                      if (name.includes('annual') || name.includes('vacation')) return { icon: Sun, color: 'blue' };
                      if (name.includes('outpatient') || (name.includes('sick') && !name.includes('hospitalisation'))) return { icon: Thermometer, color: 'amber' };
                      if (name.includes('hospitalisation')) return { icon: Hospital, color: 'red' };
                      if (name.includes('maternity')) return { icon: Baby, color: 'pink' };
                      if (name.includes('paternity')) return { icon: Baby, color: 'purple' };
                      if (name.includes('shared parental')) return { icon: Baby, color: 'fuchsia' };
                      if (name.includes('extended') && name.includes('childcare')) return { icon: Baby, color: 'emerald' };
                      if (name.includes('childcare')) return { icon: Baby, color: 'green' };
                      if (name.includes('marriage')) return { icon: Gem, color: 'rose' };
                      if (name.includes('compassionate') || name.includes('bereavement')) return { icon: Heart, color: 'gray' };
                      if (name.includes('unpaid')) return { icon: CalendarX, color: 'slate' };
                      if (name === 'off in lieu' || name.includes('oil')) return { icon: Hourglass, color: 'teal' };
                      if (name.includes('external activity') || name.includes('external')) return { icon: MapPinned, color: 'cyan' };
                      if (name.includes('work from home') || name.includes('wfh')) return { icon: Home, color: 'indigo' };
                      if (name === 'on course') return { icon: GraduationCap, color: 'violet' };
                      if (name.includes('national service')) return { icon: Shield, color: 'lime' };
                      return { icon: CalendarDays, color: 'stone' };
                    };
                    
                    const { icon: LeaveIcon, color } = getLeaveTypeIcon(balance.leave_type);
                    
                    // Get status color based on remaining days (for regular types) or usage (for special types)
                    const getStatusColor = () => {
                      if (isSpecialType) {
                        return 'text-blue-600'; // Blue for usage-based types
                      }
                      if (remainingPercentage >= 70) return 'text-green-600';
                      if (remainingPercentage >= 30) return 'text-orange-600';
                      return 'text-red-600';
                    };
                    
                    const getProgressColor = () => {
                      if (isSpecialType) {
                        return 'bg-blue-500'; // Blue for usage-based types
                      }
                      if (remainingPercentage >= 70) return 'bg-green-500';
                      if (remainingPercentage >= 30) return 'bg-orange-500';
                      return 'bg-red-500';
                    };
                    
                    return (
                      <div key={index} className="p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-200 transition-colors">
                        {/* Header with Icon and Type — items-start pins Entitlement/Earned/C/F to the leave-type title (DS Content Grid Cards) */}
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex items-start min-w-0 flex-1">
                            <div className={`w-8 h-8 flex-shrink-0 ${color === 'amber' ? 'bg-amber-100' : color === 'blue' ? 'bg-blue-100' : color === 'red' ? 'bg-red-100' : color === 'pink' ? 'bg-pink-100' : color === 'purple' ? 'bg-purple-100' : color === 'orange' ? 'bg-orange-100' : color === 'green' ? 'bg-green-100' : color === 'cyan' ? 'bg-cyan-100' : color === 'teal' ? 'bg-teal-100' : color === 'slate' ? 'bg-slate-100' : color === 'indigo' ? 'bg-indigo-100' : color === 'rose' ? 'bg-rose-100' : color === 'sky' ? 'bg-sky-100' : color === 'fuchsia' ? 'bg-fuchsia-100' : color === 'emerald' ? 'bg-emerald-100' : color === 'violet' ? 'bg-violet-100' : color === 'lime' ? 'bg-lime-100' : color === 'stone' ? 'bg-stone-100' : 'bg-gray-100'} rounded-full flex items-center justify-center mr-3`}>
                              <LeaveIcon className={`w-4 h-4 ${color === 'amber' ? 'text-amber-600' : color === 'blue' ? 'text-blue-800' : color === 'red' ? 'text-red-600' : color === 'pink' ? 'text-pink-600' : color === 'purple' ? 'text-purple-600' : color === 'orange' ? 'text-orange-600' : color === 'green' ? 'text-green-600' : color === 'cyan' ? 'text-cyan-600' : color === 'teal' ? 'text-teal-600' : color === 'slate' ? 'text-slate-600' : color === 'indigo' ? 'text-indigo-600' : color === 'rose' ? 'text-rose-600' : color === 'sky' ? 'text-sky-600' : color === 'fuchsia' ? 'text-fuchsia-600' : color === 'emerald' ? 'text-emerald-600' : color === 'violet' ? 'text-violet-600' : color === 'lime' ? 'text-lime-600' : color === 'stone' ? 'text-stone-600' : 'text-gray-600'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                              <h3 className="text-card-header font-semibold text-gray-900">{balance.leave_type}</h3>
                                {isPartTime && !isSpecialType && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
                                    Accrual
                                  </span>
                                )}
                              </div>
                              <div className="mt-1">
                                {isSpecialType ? (
                                  <>
                                    <span className={`text-2xl font-bold ${getStatusColor()}`}>{used}</span>
                                    <span className="text-sm text-gray-600 ml-1">days used this year</span>
                                  </>
                                ) : (
                                  <>
                                    {/* Home Leave when the worker has gone
                                        past the 30 cal-day planning cap.
                                        Almost always means HR approved an
                                        extension during an active trip
                                        (emergency back home). NOT a violation
                                        — frame as informational
                                        ("+X days extension granted"),
                                        amber not red. See chat 953a519e
                                        (May 2026). */}
                                    {balance.leave_type === 'Home Leave' && parseFloat(remaining) < 0 ? (
                                      <>
                                        <span className="text-2xl font-bold text-orange-600">
                                          +{formatLeaveDaysEmployee(Math.abs(parseFloat(remaining)))}
                                        </span>
                                        <span className="text-sm text-orange-700 ml-1">days extension granted</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className={`text-2xl font-bold ${getStatusColor()}`}>{formatLeaveDaysEmployee(remaining)}</span>
                                        <span className="text-sm text-gray-600 ml-1">
                                          days available
                                        </span>
                                      </>
                                    )}
                                    {/* Home Leave: 1 Jan reset reminder for
                                        at-cap and extended states. */}
                                    {balance.leave_type === 'Home Leave' && parseFloat(balance.entitled_days || 0) > 0 && parseFloat(remaining) > 0 && (
                                      <span className="text-xs text-gray-500 mt-1 block">
                                        Trips starting in {new Date().getFullYear() + 1} use that year's cap. You can apply now.
                                      </span>
                                    )}
                                    {balance.leave_type === 'Home Leave' && parseFloat(balance.entitled_days || 0) > 0 && parseFloat(remaining) <= 0 && (
                                      <span className="text-xs text-gray-500 mt-1 block">
                                        Cap resets on 1 Jan {new Date().getFullYear() + 1}. You can apply for {new Date().getFullYear() + 1} now.
                                      </span>
                                    )}
                                    {isAccrualBased && parseFloat(balance.accrued_days || 0) < parseFloat(balance.entitled_days || 0) && (() => {
                                      const nextDate = getNextAccrualDate(user?.employment_start_date, isPartTime);
                                      return nextDate ? (
                                        <span className="text-xs text-gray-400 mt-0.5 block">
                                          Next accrual: {formatAccrualDate(nextDate)}
                                        </span>
                                      ) : null;
                                    })()}
                                    {/* OIL auto-consumption hint */}
                                    {balance.leave_type === 'Off in Lieu' && parseFloat(remaining) > 0 && (
                                      <span className="text-xs text-teal-600 mt-1 block">
                                        Auto-used with AL
                                      </span>
                                    )}
                                    {/* Show unpaid indicator ONLY for employees with < 3 months service (excludes system accounts) */}
                                    {parseFloat(balance.entitled_days || 0) === 0 && parseFloat(remaining) === 0 && 
                                     (balance.leave_type.includes('Sick') || balance.leave_type.includes('Hospitalisation')) &&
                                     !isSystemAccount(user) && 
                                     getCompletedServiceMonths(user?.employment_start_date) < 3 && (
                                      <div className="text-xs text-orange-600 mt-1 font-medium">
                                        Not yet entitled (probation) - Will be processed as unpaid leave
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0">
                            {isSpecialType ? null : (
                              <>
                                {/* "Extended" badge: fires for Home Leave
                                    when the worker's calendar-year HL usage
                                    went past the 30 cal-day planning cap
                                    because HR approved an extension. Normal
                                    business, NOT a violation. Uses InfoBadge
                                    per DS (category badges must NOT use
                                    inline Tailwind) with canonical
                                    "Growth/Activity" orange tone. Same
                                    surface on the worker Leave page. See
                                    design-system-reference.md §Badge &
                                    Status Indicators and chat 953a519e
                                    (May 2026). */}
                                {parseFloat(remaining) < 0 && parseFloat(balance.entitled_days || 0) > 0 && !isUnpaid && (
                                  <div className="mb-1">
                                    <InfoBadge color="orange" label="Extended" />
                                  </div>
                                )}
                                {/* "Exhausted" badge: at cap, but not over. */}
                                {parseFloat(remaining) === 0 && parseFloat(balance.entitled_days || 0) > 0 && !isUnpaid && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mb-1">
                                    Exhausted
                                  </span>
                                )}
                              </>
                            )}
                            <div className="text-right text-sm text-gray-500">
                              {isSpecialType ? null : (
                                <>
                                  <div className="flex items-center justify-end gap-1">
                                    <CalendarDays className="w-3 h-3 sm:hidden flex-shrink-0" />
                                    <span className="sm:hidden whitespace-nowrap">
                                      Entitlement: {formatLeaveDaysEmployee(isAccrualBased ? balance.entitled_days : total)}
                                    </span>
                                    <span className="hidden sm:inline">
                                      {isAccrualBased
                                        ? (isPartTime ? 'FT-equivalent cap:' : 'Annual entitlement:')
                                        : (balance.expiry_date ? 'Total entitlement:' : 'Annual entitlement:')}{' '}
                                      {formatLeaveDaysEmployee(isAccrualBased ? balance.entitled_days : total)}
                                    </span>
                                  </div>
                                  {isAccrualBased && (
                                    <div className="text-xs text-blue-600 flex items-center justify-end gap-1">
                                      <TrendingUp className="w-3 h-3 sm:hidden flex-shrink-0" />
                                      <span className="sm:hidden">
                                        Earned: {formatLeaveDaysEmployee(balance.accrued_days || 0)}
                                      </span>
                                      <span className="hidden sm:inline">
                                        Earned: {formatLeaveDaysEmployee(balance.accrued_days || 0)} days
                                      </span>
                                    </div>
                                  )}
                                  {isAccrualBased && parseFloat(balance.carried_forward_days || 0) > 0 && (
                                    <div className="text-xs text-green-600 flex items-center justify-end gap-1">
                                      <RotateCcw className="w-3 h-3 sm:hidden flex-shrink-0" />
                                      <span className="sm:hidden">
                                        B/F: {formatLeaveDaysEmployee(balance.carried_forward_days)}
                                      </span>
                                      <span className="hidden sm:inline">
                                        Brought forward: {formatLeaveDaysEmployee(balance.carried_forward_days)} days
                                      </span>
                                    </div>
                                  )}
                                  {(balance.leave_type === 'Outpatient Sick Leave' || balance.leave_type === 'Hospitalisation Leave') && medicalLeavePool.total > 0 && (
                                    <div className="text-xs text-purple-600 flex items-center justify-end gap-1">
                                      <Hospital className="w-3 h-3 sm:hidden flex-shrink-0" />
                                      <span className="sm:hidden">
                                        Pool: {formatLeaveDaysEmployee(medicalLeavePool.remaining)}/{formatLeaveDaysEmployee(medicalLeavePool.total)}
                                      </span>
                                      <span className="hidden sm:inline">
                                        Medical leave: {formatLeaveDaysEmployee(medicalLeavePool.remaining)} of {formatLeaveDaysEmployee(medicalLeavePool.total)} remaining
                                      </span>
                                    </div>
                                  )}
                                  {/* OIL expiry - FIFO-aware */}
                                  {balance.leave_type === 'Off in Lieu' && (() => {
                                    const hint = buildOilExpiryHint(balance);
                                    if (!hint) return null;
                                    return (
                                      <div className={`text-xs font-medium flex items-center justify-end gap-1 ${hint.isExpiringSoon ? 'text-orange-600' : 'text-teal-600'}`}>
                                        {hint.isExpiringSoon && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
                                        <span>{hint.text}</span>
                                      </div>
                                    );
                                  })()}
                                  {balance.trigger_event && balance.trigger_event !== 'none' && (
                                    <div className="text-xs text-blue-600 capitalize">
                                      {balance.trigger_event.replace('_', ' ')} required
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {balance.leave_type === 'Annual Leave'
                          && yearEndPlan?.visible
                          && yearEndPlan.risk_band !== 'safe' && (
                          <div className="mt-3">
                            <AnnualLeaveYearEndBalance plan={yearEndPlan} compact />
                          </div>
                        )}
                        
                        {/* Progress Bar - Only show for regular leave types */}
                        {!isSpecialType && (
                          <div className="space-y-2">
                            <div className="flex justify-end text-xs text-gray-600">
                              <span>Used: {formatLeaveDaysEmployee(used)} days</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                                style={{ width: `${calculateProgressBarWidth(balance, total, remainingPercentage)}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
                );
              })()
            ) : (
              <div className={`${DASHBOARD_CARD_CONTENT_MAX_HEIGHT} flex items-center justify-center`}>
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wallet className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-card-header text-gray-600">No leave balances found</p>
                  <p className="text-small mb-4">This could be due to:</p>
                  <div className="text-xs text-gray-500 space-y-1 mb-4">
                    <p>• Insufficient service time (3+ months required)</p>
                    <p>• Missing document approvals (birth certificate, etc.)</p>
                    <p>• Leave entitlements not yet configured</p>
                  </div>
                  <div className="space-y-2">
                    <Link 
                      to="/profile" 
                      className="btn btn-primary block"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Update Profile
                    </Link>
                    <Link 
                      to="/leave" 
                      className="btn btn-secondary block"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Check Leave Eligibility
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Approval Details Modal - For ROs to review leave applications before approving/rejecting */}
      {approvalDetailsModal.open && approvalDetailsModal.leave && (
        <Portal>
          <div className="modal-overlay">
            <div className="modal-container modal-container-md">
              <div className="modal-header">
                <h3 className="modal-title">Leave Application Details</h3>
                <button 
                  onClick={() => setApprovalDetailsModal({ open: false, leave: null, rejecting: false, rejectionReason: '' })} 
                  className="modal-close"
                  disabled={approvalActionLoading}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="modal-content">
                {(() => {
                  const leave = approvalDetailsModal.leave;
                  const appliedType = leave.leave_type?.leave_type_name || leave.leave_type_name || 'Leave';
                  const modalProposal = leave.leave_consumption_proposal;
                  const leaveTypeName = (modalProposal?.items?.length === 1 && modalProposal.items[0].leave_type !== appliedType)
                    ? modalProposal.items[0].leave_type : appliedType;
                  const config = getLeaveTypeConfig(appliedType);

                  return (
                    <div className="space-y-4">
                      {/* Leave Type and Employee Info — items-start pins StatusBadge to title baseline */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-lg font-semibold text-gray-900">{leaveTypeName}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {leave.staff_name || leave.employee_name || 'Employee'}
                            {(leave.staff_code || leave.employee_id || leave.staff_id) && (
                              <span className="text-gray-400 ml-1">· {leave.staff_code || leave.employee_id || leave.staff_id}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <StatusBadge status={leave.leave_status || leave.status} />
                        </div>
                      </div>

                      {/* Leave Dates - Smart date range */}
                      <div>
                        <label className="text-sm font-medium text-gray-700">Date</label>
                        <div className="mt-1 flex items-center flex-wrap gap-1">
                          <Calendar className="w-4 h-4 text-purple-600 mr-1" />
                          {(() => {
                            const startDate = leave.leave_start_date || leave.start_date;
                            const endDate = leave.leave_end_date || leave.end_date;
                            const isMultiDay = endDate && String(startDate) !== String(endDate);
                            const startYear = getYearInSingapore(startDate);
                            const endYear = getYearInSingapore(endDate);
                            const sameYear = startYear === endYear;
                            
                            return (
                              <>
                                <span className="text-sm text-gray-900">
                                  {isMultiDay && sameYear ? formatDateShort(startDate) : formatDate(startDate)}
                                </span>
                                <SessionPeriodChip session={getLeavePeriodDisplayChips(leave).start} />
                                {isMultiDay && (
                                  <>
                                    <span className="text-gray-400 mx-1">—</span>
                                    <span className="text-sm text-gray-900">{formatDate(endDate)}</span>
                                    <SessionPeriodChip session={getLeavePeriodDisplayChips(leave).end} />
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Duration */}
                      <div>
                        <label className="text-sm font-medium text-gray-700">Duration</label>
                        <div className="mt-1 flex items-center flex-wrap">
                          {(() => {
                            const isOilClaim = appliedType === 'Claim: Off in Lieu';
                            const oilDays = isOilClaim && (
                              leave.oil_dates?.reduce((s, e) => s + (parseFloat(e.days) || 0), 0) ||
                              leave.oil_credited_days || 0
                            );
                            if (isOilClaim && oilDays) {
                              const oilStatus = leave.leave_status || leave.status;
                              const isDead = oilStatus === 'rejected' || oilStatus === 'cancelled';
                              return (
                                <>
                                  <Clock className={`w-4 h-4 mr-2 ${isDead ? 'text-gray-400' : 'text-teal-500'}`} />
                                  <span className={`text-sm ${getOilCreditDaysClassName(oilStatus)}`}>
                                    {formatOilCreditDaysLabel(oilDays, oilStatus)}
                                  </span>
                                </>
                              );
                            }
                            const days = getDisplayTotalDays(leave);
                            return (
                              <>
                                <Clock className="w-4 h-4 text-orange-500 mr-2" />
                                <span className="text-sm text-gray-900">{formatLeaveDays(days)} {pluralizeDays(days)}</span>
                              </>
                            );
                          })()}
                          {hasCrossType(leave, getAppliedLeaveTypeName(leave)) && (
                            <ConsumptionBreakdown
                              proposal={leave.leave_consumption_proposal}
                              className="ml-2"
                            />
                          )}
                        </div>
                      </div>

                      {/* Type-specific content sections, driven by LEAVE_TYPE_CONFIG */}
                      {config.sections.map(section => renderLeaveSection(section, leave))}

                      <OverseasTravelDeclarationSummary application={leave} audience="approver" />

                      {/* Team availability during this period */}
                      <div className="border-t border-gray-200 pt-3">
                        <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-500" />
                          Team availability
                        </h5>
                        {overlappingLoading ? (
                          <div className="flex items-center gap-2 py-2">
                            <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            <span className="text-xs text-gray-500">Checking team schedule…</span>
                          </div>
                        ) : overlappingTeamLeaves.length === 0 ? (
                          <div className="flex items-center gap-2 py-2 px-3 bg-green-50 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-sm text-green-800">No one else on your team is on leave during this period</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 py-1.5 px-3 bg-amber-50 rounded-lg">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                              <span className="text-xs text-amber-800">
                                {overlappingTeamLeaves.length} team {overlappingTeamLeaves.length === 1 ? 'member' : 'members'} also on leave during this period
                              </span>
                            </div>
                            <div className="space-y-1.5">
                              {overlappingTeamLeaves.slice(0, 6).map((ol) => {
                                const olStart = ol.leave_start_date;
                                const olEnd = ol.leave_end_date;
                                const isSameDay = olStart === olEnd;
                                return (
                                  <div key={ol.id} className="text-sm py-1.5 px-3 bg-gray-50 rounded-lg">
                                    {/* Name is the primary detail for the RO — give it the full row width. */}
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="font-medium text-gray-900 truncate min-w-0" title={ol.staff_name}>
                                        {ol.staff_name}
                                      </span>
                                      <span className="flex-shrink-0">
                                        <StatusBadge status={ol.leave_status} />
                                      </span>
                                    </div>
                                    <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 mt-1">
                                      <InfoBadge color="blue" label={ol.leave_type_name || 'Leave'} />
                                      <span className="text-xs text-gray-500 whitespace-nowrap">
                                        {isSameDay ? formatDateShort(olStart) : `${formatDateShort(olStart)} – ${formatDateShort(olEnd)}`}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                              {overlappingTeamLeaves.length > 6 && (
                                <p className="text-xs text-gray-500 text-center pt-1">
                                  +{overlappingTeamLeaves.length - 6} more — check the Leave Calendar for full details
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Application Timeline */}
                      <div className="border-t border-gray-200 pt-3">
                        <h5 className="text-sm font-semibold text-gray-900 mb-2">Application Timeline</h5>
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                            <div className="flex-1 flex items-center justify-between">
                              <span className="text-sm text-gray-700">Application Submitted</span>
                              <span className="text-xs text-gray-500">
                                {leave.leave_applied_at ? formatDateTime(leave.leave_applied_at) : 'Not available'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>


                    </div>
                  );
                })()}
              </div>
              
              {/* Approval action footer — intentionally NOT using .modal-footer (which has justify-end for right-aligned
                  natural-width buttons). Approval/rejection are binary full-width primary actions requiring
                  equal-weight buttons that fill the available space. */}
              <div className="flex-shrink-0 flex flex-col gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50/50">
                {(() => {
                  const modalLeave = approvalDetailsModal.leave;
                  const isNS = isNationalServiceLeave(modalLeave);
                  const leaveStatus = (modalLeave.leave_status || '').toLowerCase();
                  const alreadyAcknowledged = isAcknowledgedNsLeave(modalLeave);

                  if (isNS) {
                    return (
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          {alreadyAcknowledged && (
                            <StatusBadge status="acknowledged" />
                          )}
                          {leaveStatus === 'approved' && (
                            <StatusBadge status="approved" label="Verified & Approved by HR" />
                          )}
                          {leaveStatus === 'rejected' && (
                            <StatusBadge status="rejected" label="Rejected by HR" />
                          )}
                          {leaveStatus === 'submitted' && (
                            <StatusBadge status="pending" label="Pending HR Verification" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 text-center">
                          {alreadyAcknowledged
                            ? `You acknowledged this on ${formatDateTime(modalLeave.ro_acknowledged_at)}. HR still verifies the call-up, so it can stay under Pending Verification.`
                            : 'Acknowledging records that you know about this call-up and removes it from your queue. HR still checks the order before the leave is confirmed.'}
                        </p>
                        {!alreadyAcknowledged && (
                          <button
                            type="button"
                            onClick={() => handleAcknowledge(modalLeave.id)}
                            disabled={approvalActionLoading}
                            className="btn btn-primary w-full"
                          >
                            {approvalActionLoading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                            ) : (
                              <Shield className="w-4 h-4 mr-2" aria-hidden="true" />
                            )}
                            {approvalActionLoading ? 'Acknowledging…' : 'Acknowledge'}
                          </button>
                        )}
                      </div>
                    );
                  }

                  return null;
                })()}
                {(() => {
                  const modalLeave = approvalDetailsModal.leave;
                  if (isNationalServiceLeave(modalLeave)) return null;

                  if (requiresDocumentVerification(modalLeave)) {
                    return (
                      <div className="flex justify-center">
                        <StatusBadge status="pending" label="Awaiting HR Verification" />
                      </div>
                    );
                  }
                  if (approvalDetailsModal.rejecting) {
                    return (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for rejection <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                            rows={3}
                            placeholder="Provide a reason so the employee can understand the decision..."
                            value={approvalDetailsModal.rejectionReason ?? ''}
                            onChange={(e) => setApprovalDetailsModal(prev => ({ ...prev, rejectionReason: e.target.value }))}
                            autoFocus={!isMobile}
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => setApprovalDetailsModal(prev => ({ ...prev, rejecting: false, rejectionReason: '' }))}
                            className="btn btn-outline"
                            disabled={approvalActionLoading}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={async () => {
                              const reason = (approvalDetailsModal.rejectionReason ?? '').trim();
                              if (!reason) { toast.error('Please provide a rejection reason.'); return; }
                              const ok = await handleReject(modalLeave.id, reason);
                              if (ok) {
                                setApprovalDetailsModal({ open: false, leave: null, rejecting: false, rejectionReason: '' });
                              }
                            }}
                            className="btn btn-danger"
                            disabled={approvalActionLoading || !(approvalDetailsModal.rejectionReason ?? '').trim()}
                          >
                            {approvalActionLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <XCircle className="w-4 h-4" aria-hidden="true" />
                            )}
                            {approvalActionLoading ? 'Rejecting…' : 'Confirm Rejection'}
                          </button>
                        </div>
                      </>
                    );
                  }
                  return (
                    <div className="flex gap-3 btn-row-spread">
                      <button
                        onClick={() => setApprovalDetailsModal(prev => ({ ...prev, rejecting: true, rejectionReason: prev.rejectionReason ?? '' }))}
                        className="btn btn-danger"
                        disabled={approvalActionLoading}
                      >
                        <X className="w-4 h-4 mr-2" aria-hidden="true" />
                        Reject
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await handleApprove(modalLeave.id);
                          if (ok) {
                            setApprovalDetailsModal({ open: false, leave: null, rejecting: false, rejectionReason: '' });
                          }
                        }}
                        className="btn btn-success"
                        disabled={approvalActionLoading}
                      >
                        {approvalActionLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                        )}
                        {approvalActionLoading ? 'Approving…' : 'Approve'}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Document Verification Modal */}
      {/* key prop forces React to remount modal when documentApprovalId changes, resetting all internal state */}
      <DocumentVerificationModal
        key={verificationModal.documentApprovalId}
        isOpen={verificationModal.open}
        onClose={() => setVerificationModal({ open: false, documentApprovalId: null, documentType: null, employeeName: null, employeeStaffCode: null })}
        documentApprovalId={verificationModal.documentApprovalId}
        documentType={verificationModal.documentType || 'birth_certificate'}
        mode="hr"
        onApprovalComplete={handleVerificationComplete}
        applicationId={verificationModal.applicationId || null}
        employeeName={verificationModal.employeeName}
        employeeStaffCode={verificationModal.employeeStaffCode}
      />

      {/* NS Call-up Order Verification Modal */}
      <NSDocumentVerificationModal
        key={nsVerificationModal.documentApprovalId}
        isOpen={nsVerificationModal.open}
        onClose={() => setNsVerificationModal({ open: false, documentApprovalId: null, leaveApplicationId: null, employeeName: null, employeeStaffCode: null })}
        documentApprovalId={nsVerificationModal.documentApprovalId}
        leaveApplicationId={nsVerificationModal.leaveApplicationId}
        onVerificationComplete={handleNSVerificationComplete}
        employeeName={nsVerificationModal.employeeName}
        employeeStaffCode={nsVerificationModal.employeeStaffCode}
      />

      {/* MC Document Verification Modal (Outpatient Sick / Hospitalisation) */}
      <MCDocumentVerificationModal
        key={mcVerificationModal.applicationId}
        isOpen={mcVerificationModal.open}
        onClose={() => setMcVerificationModal({ open: false, applicationId: null, application: null })}
        applicationId={mcVerificationModal.applicationId}
        application={mcVerificationModal.application}
        onVerificationComplete={handleMCVerificationComplete}
      />

      {/* Flight Ticket Verification Modal (Home Leave / Urgent Leave) */}
      {flightTicketModal.open && (
        <FlightTicketVerificationModal
          key={flightTicketModal.documentApprovalId}
          isOpen={flightTicketModal.open}
          onClose={() => setFlightTicketModal({ open: false, documentApprovalId: null, leaveApplication: null })}
          documentApprovalId={flightTicketModal.documentApprovalId}
          leaveApplication={flightTicketModal.leaveApplication}
          onVerificationComplete={() => {
            setFlightTicketModal({ open: false, documentApprovalId: null, leaveApplication: null });
            queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
          }}
        />
      )}

      {/* RO Exception Resolution Modal */}
      <ROExceptionModal
        isOpen={exceptionModal.open}
        exception={exceptionModal.exception}
        onClose={() => setExceptionModal({ open: false, exception: null })}
        onSuccess={handleExceptionResolved}
      />
    </div>
  );
};

export default Dashboard; 